import { plural, t } from '@lingui/core/macro';
import { differenceInCalendarDays, format, parseISO } from 'date-fns';
import { ActivePaidSubscription } from '../interfaces/active-paid-subscription';
import { SUBSCRIPTION_EXPIRY_NOTICE } from '../constants/subscription-expiry-notice';

/** Что мы запомнили о последнем показе тоста для конкретного турнира. */
export interface SubscriptionExpiryNoticeRecord {
  /** activeTo подписки, про которую предупреждали. Смена значения = новый период. */
  activeTo: string;
  /** Date.now() момента показа. */
  shownAt: number;
}

export type SubscriptionExpiryNoticeDecision =
  | { kind: 'skip'; reason: 'no-subscription' | 'invalid-date' | 'not-expiring-soon' | 'cooldown' }
  | { kind: 'show'; daysLeft: number; activeTo: string };

export function decideSubscriptionExpiryNotice(params: {
  subscription: ActivePaidSubscription | null | undefined;
  lastNotice: SubscriptionExpiryNoticeRecord | null;
  now: Date;
}): SubscriptionExpiryNoticeDecision {
  const { subscription, lastNotice, now } = params;

  if (!subscription?.activeTo) return { kind: 'skip', reason: 'no-subscription' };

  const activeToDate = parseISO(subscription.activeTo);
  if (Number.isNaN(activeToDate.getTime())) return { kind: 'skip', reason: 'invalid-date' };

  const daysLeft = differenceInCalendarDays(activeToDate, now);
  // daysLeft < 0 недостижим через activePaidSubscription (для истёкшей подписки он отдаёт null,
  // и мы выйдем выше по ветке no-subscription). Проверка оставлена защитой на случай смены
  // источника данных на getOrganizerSubscriptionSummary — см. §7.1 плана.
  if (daysLeft < 0 || daysLeft > SUBSCRIPTION_EXPIRY_NOTICE.warnWithinDays) {
    return { kind: 'skip', reason: 'not-expiring-soon' };
  }

  // Cooldown действует только внутри одного периода подписки: продлили → activeTo другой → показываем.
  if (lastNotice && lastNotice.activeTo === subscription.activeTo) {
    const hoursSinceShown = (now.getTime() - lastNotice.shownAt) / (1000 * 60 * 60);
    if (hoursSinceShown < SUBSCRIPTION_EXPIRY_NOTICE.cooldownHours) {
      return { kind: 'skip', reason: 'cooldown' };
    }
  }

  return { kind: 'show', daysLeft, activeTo: subscription.activeTo };
}

export function formatSubscriptionExpiryMessage(daysLeft: number, activeTo: string): string {
  const until = format(parseISO(activeTo), 'dd.MM.yyyy');
  if (daysLeft === 0) return t`Подписка заканчивается сегодня (${until})`;
  // Склонение — ICU plural каталога, а не ручной выбор окончания: у каждого языка свои формы.
  return t`Подписка заканчивается через ${plural(daysLeft, { one: '# день', few: '# дня', many: '# дней', other: '# дня' })} (${until})`;
}
